import { getAccessibleTours } from './tourSelection'
import type { OnboardingStateMap, OnboardingTourDefinition } from './types'

export type OnboardingTrainingItem = {
  id: string
  title: string
  description: string
  icon: string
  isDone: boolean
  needsRedo: boolean
  statusLabel: string
  doneAt: string | null
  doneVersion: number | null
  currentVersion: number
  entryPath: string
  canStart: boolean
  startDisabledReason: string | null
  isDisabled: boolean
}

function parseIsoDateLabel(isoDate: string | undefined): string | null {
  if (!isoDate) return null
  const timestamp = Date.parse(isoDate)
  if (Number.isNaN(timestamp)) return null
  try {
    return new Date(timestamp).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return null
  }
}

function computeStatusLabel(hasProgress: boolean, needsRedo: boolean, isDone: boolean): string {
  if (!hasProgress) return 'À faire'
  if (needsRedo) return 'À refaire'
  if (isDone) return 'Fait'
  return 'En cours'
}

function buildTrainingItem(
  tour: OnboardingTourDefinition,
  resolvedState: OnboardingStateMap,
  blocker: string | null,
  disabledTourIds: ReadonlySet<string>
): OnboardingTrainingItem {
  const progress = resolvedState[tour.id]
  const isFinished = progress?.status === 'completed'
  const hasProgress = progress !== undefined
  const doneVersion = hasProgress ? progress.version : null
  const doneAt = isFinished ? parseIsoDateLabel(progress?.updatedAt) : null
  const needsRedo = Boolean(
    tour.forceRedoOnUpdate && isFinished && typeof doneVersion === 'number' && doneVersion < tour.version
  )
  const isDone = Boolean(isFinished && (!needsRedo || !tour.forceRedoOnUpdate))
  const isDisabled = disabledTourIds.has(tour.id)
  const startDisabledReason = blocker ?? (isDisabled ? 'Ce guide est désactivé.' : null)

  return {
    id: tour.id,
    title: tour.displayTitle,
    description: tour.displayDescription,
    icon: tour.icon,
    isDone,
    needsRedo,
    statusLabel: computeStatusLabel(hasProgress, needsRedo, isDone),
    doneAt,
    doneVersion,
    currentVersion: tour.version,
    entryPath: tour.entryPath,
    canStart: startDisabledReason === null,
    startDisabledReason,
    isDisabled,
  }
}

export function listAccessibleTrainings(
  roles: string[],
  resolvedState: OnboardingStateMap,
  blocker: string | null,
  disabledTourIds: ReadonlySet<string> = new Set()
): OnboardingTrainingItem[] {
  return getAccessibleTours(roles).map(tour =>
    buildTrainingItem(tour, resolvedState, blocker, disabledTourIds)
  )
}
